// AdminCohorts — Phase 9. Rule-based user segmentation + geography sync view.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Users, Trash2, Plus } from 'lucide-react';

export default function AdminCohorts() {
    const [cohorts, setCohorts] = useState([]);
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [role, setRole] = useState('');
    const [region, setRegion] = useState('');
    const [department, setDepartment] = useState('');
    const [tags, setTags] = useState('');
    const [previewing, setPreviewing] = useState(null);

    const load = useCallback(async () => {
        try { const r = await api.get('/admin/cohorts'); setCohorts(r.data || []); }
        catch { toast.error('Failed to load'); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!name.trim()) { toast.error('Name required'); return; }
        const rules = {};
        if (role) rules.role = role;
        if (region) rules.region = region;
        if (department) rules.department = department;
        if (tags) rules.tags = tags.split(',').map(s => s.trim()).filter(Boolean);
        try {
            await api.post('/admin/cohorts', { name, description: desc, rules });
            setName(''); setDesc(''); setRole(''); setRegion(''); setDepartment(''); setTags('');
            toast.success('Cohort created');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    };

    const del = async (id) => {
        if (!window.confirm('Delete cohort?')) return;
        try { await api.delete(`/admin/cohorts/${id}`); load(); } catch { toast.error('Delete failed'); }
    };

    const preview = async (c) => {
        try { const r = await api.get(`/admin/cohorts/${c.id}/members`); setPreviewing(r.data); }
        catch { toast.error('Could not load members'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-cohorts-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900">Cohorts &amp; Segmentation</h1>
                <p className="text-sm text-slate-500">Rule-based audience segments. Used by pathways &amp; notifications.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3">
                    <div className="font-bold text-slate-800 flex items-center gap-2"><Plus className="w-4 h-4" /> New Cohort</div>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cohort name (e.g. Mumbai Electricians)" className={inputCls} data-testid="cohort-name" />
                    <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" rows="2" className={inputCls} data-testid="cohort-desc" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role (e.g. electrician)" className={inputCls} data-testid="cohort-role" />
                        <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region" className={inputCls} data-testid="cohort-region" />
                        <input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Department" className={inputCls} data-testid="cohort-dept" />
                        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags (comma-separated)" className={inputCls} data-testid="cohort-tags" />
                    </div>
                    <button onClick={create} className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm" data-testid="cohort-create-btn">Create Cohort</button>
                </div>

                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                    <div className="flex items-center gap-2 font-bold text-slate-800 mb-3"><Users className="w-4 h-4" /> Active ({cohorts.length})</div>
                    {cohorts.length === 0 ? <div className="text-xs text-slate-400 italic">No cohorts</div> : (
                        <div className="space-y-2">
                            {cohorts.map(c => (
                                <div key={c.id} className="ring-1 ring-slate-200 rounded-xl p-3 flex items-center gap-2" data-testid={`cohort-row-${c.id}`}>
                                    <div className="flex-1">
                                        <div className="font-bold text-sm">{c.name}</div>
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{Object.entries(c.rules || {}).map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('/') : v}`).join(' · ') || 'no rules'}</div>
                                    </div>
                                    <span className="text-xs font-bold text-slate-600">{c.member_count} members</span>
                                    <button onClick={() => preview(c)} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200" data-testid={`cohort-preview-${c.id}`}>Preview</button>
                                    <button onClick={() => del(c.id)} className="text-rose-600 hover:text-rose-800" data-testid={`cohort-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {previewing && (
                <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setPreviewing(null)}>
                    <div className="bg-white rounded-2xl w-full max-w-2xl p-5 ring-1 ring-slate-200 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cohort-preview-modal">
                        <div className="font-bold text-lg mb-2">{previewing.cohort.name} <span className="text-xs text-slate-500">· {previewing.member_count} members</span></div>
                        <div className="max-h-96 overflow-y-auto space-y-1">
                            {previewing.members.map(m => (
                                <div key={m.id} className="text-xs ring-1 ring-slate-100 rounded p-2">
                                    <span className="font-bold">{m.full_name}</span> · {m.email} · {m.role} · {m.region || '—'}
                                </div>
                            ))}
                            {previewing.members.length === 0 && <div className="text-xs text-slate-400 italic">No matching members</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';
